/**
 * <s-sticky-atc> — mobile sticky add-to-cart bar for the product page.
 *
 * Shows its [data-sticky-bar] once the main add button (button[data-main-atc] inside the
 * closest <s-product-form>) has scrolled above the viewport, and hides it again when the
 * main button is back in view. The bar's [data-sticky-submit] button submits the real
 * product form with the main add button as submitter, so the existing <s-product-form>
 * add-to-cart flow (and its no-JS fallback) is reused unchanged.
 * Does nothing when rendered inside a dialog (e.g. a quick-add modal).
 */
class StickyAtc extends HTMLElement {
  #abort = null;
  #observer = null;

  connectedCallback() {
    if (this.closest('dialog')) return;
    const host = this.closest('s-product-form');
    const mainButton = host?.querySelector('[data-main-atc]');
    const bar = this.querySelector('[data-sticky-bar]');
    if (!host || !bar || !mainButton) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#observer = new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.#toggle(bar, scrolledPast);
    });
    this.#observer.observe(mainButton);

    this.querySelector('[data-sticky-submit]')?.addEventListener(
      'click',
      () => {
        const form = mainButton.closest('form');
        if (form instanceof HTMLFormElement && mainButton instanceof HTMLButtonElement) {
          form.requestSubmit(mainButton);
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  #toggle(bar, show) {
    bar.toggleAttribute('data-visible', show);
    bar.inert = !show;
  }
}

if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', StickyAtc);
